import { MAP_BASE_LAYERS, RESULT_CATEGORIES } from '@/utils/constants'
import { createStore } from '@/utils/create-store.ts'
import type { BBox } from 'geojson'
import type mapboxgl from 'mapbox-gl'

const filterCategories = (blackList: string[]) =>
  Object.fromEntries(
    Object.entries(RESULT_CATEGORIES).filter(
      ([value]) => !blackList.includes(value),
    ),
  )

export const HEX_CATEGORIES = filterCategories([
  RESULT_CATEGORIES.INSIGHT,
  RESULT_CATEGORIES.RELATED_IMAGERY,
])
export type HexGridCellPropertyResultKeys = typeof HEX_CATEGORIES
export type HexGridCellProperties = {
  id: number
  resultIds: { [k in ValueOf<HexGridCellPropertyResultKeys>]: string[] }
  resultCount: number
}

export type VisualizationModeOptions = 'grid' | 'extents' | 'none'

export type BaseLayerOption =
  (typeof MAP_BASE_LAYERS)[keyof typeof MAP_BASE_LAYERS]
export type GridCellProperties = {
  [key: string]: HexGridCellProperties
}

export interface MapState {
  visualizationMode: VisualizationModeOptions
  activeLayers: string[]
  baseLayer: BaseLayerOption
  gridCellProperties: {
    [key: string]: HexGridCellProperties
  }

  currentMapBounds: mapboxgl.LngLatBounds | null
  selectedHexGridId: number | null
  selectedPointId: string | null
  hoveredHexGridId: number | null
  selectedExtentIds: string[]
  hoveredExtentIds: string[]
  setVisualizationMode: (mode: VisualizationModeOptions) => void
  setActiveLayers: (layer: string) => void
  setBaseLayer: (baseLayer: BaseLayerOption) => void
  setCurrentMapBounds: (currentMapBounds: mapboxgl.LngLatBounds | null) => void
  toggleSelectedHexGridId: (selectedHexGridId: number | null) => void
  setHoveredHexGridId: (hoveredHexGridId: number | null) => void
  setSelectedPointId: (selectedPointId: string | null) => void
  setSelectedExtentIds: (extentIds: string[]) => void
  setHoveredExtentIds: (extentIds: string[]) => void
  setGridCellProperties: (gridCellProperties: GridCellProperties) => void

  activeOrthoLayerGroupIds: string[]
  setActiveOrthoLayerGroupIds: (ids: string[]) => void
  toggleOrthoLayerGroupId: (id: string) => void
  clearOrthoLayerGroupIds: () => void
  clearActiveLayers: () => void
  zoomOrthoLayerGroupId: string | null

  hoveredLayerId: string | null
  setHoveredLayerId: (prefix: string | null) => void

  mapBbox: BBox | null
  setMapBbox: (bbox: BBox | null) => void

  pointViewPort: number[] | null
  setPointViewPort: (viewPort: number[] | null) => void
  zoomToOrthoLayer: (layerId: string) => void
}

export const useMapStore = createStore<MapState>({
  initialState: {
    visualizationMode: 'extents',
    activeLayers: ['Point'],
    baseLayer: MAP_BASE_LAYERS.dantiStreets,
    gridCellProperties: {},
    currentMapBounds: null,
    hoveredHexGridId: null,
    selectedHexGridId: null,
    selectedExtentIds: [],
    hoveredExtentIds: [],
    activeOrthoLayerGroupIds: [],
    zoomOrthoLayerGroupId: null,
    hoveredLayerId: null,
    selectedPointId: null,
    mapBbox: null,
    pointViewPort: null,
  },
  actions: (setState, getState) => ({
    setVisualizationMode: (visualizationMode) =>
      setState({ visualizationMode }),
    setActiveLayers: (layer: string) => {
      const activeLayers = new Set(getState().activeLayers)
      activeLayers?.has(layer)
        ? activeLayers?.delete(layer)
        : activeLayers?.add(layer)
      setState({
        activeLayers: [...activeLayers],
      })
    },
    setBaseLayer: (baseLayer: BaseLayerOption) => setState({ baseLayer }),
    setCurrentMapBounds: (currentMapBounds: mapboxgl.LngLatBounds | null) =>
      setState({ currentMapBounds }),
    toggleSelectedHexGridId: (selectedHexGridId: number | null) =>
      setState({
        selectedHexGridId:
          getState().selectedHexGridId === selectedHexGridId
            ? null
            : selectedHexGridId,
      }),
    setHoveredHexGridId: (hoveredHexGridId: number | null) =>
      setState({ hoveredHexGridId }),
    setSelectedPointId: (selectedPointId: string | null) =>
      setState({ selectedPointId }),
    setHoveredExtentIds: (extentIds: string[]) =>
      setState({ hoveredExtentIds: extentIds }),
    setSelectedExtentIds: (extentIds: string[]) =>
      setState({ selectedExtentIds: extentIds }),
    setGridCellProperties: (gridCellProperties: GridCellProperties) =>
      setState({ gridCellProperties }),
    setActiveOrthoLayerGroupIds: (ids: string[]) =>
      setState({ activeOrthoLayerGroupIds: [...ids] }),
    toggleOrthoLayerGroupId: (id: string) => {
      const activeOrthoLayerIds = new Set(getState().activeOrthoLayerGroupIds)
      activeOrthoLayerIds?.has(id)
        ? activeOrthoLayerIds?.delete(id)
        : activeOrthoLayerIds?.add(id)
      setState({
        activeOrthoLayerGroupIds: [...activeOrthoLayerIds],
      })
    },
    zoomToOrthoLayer: (layerId: string) => {
      setState({
        zoomOrthoLayerGroupId: layerId,
      })
    },
    clearActiveLayers: () => setState({ activeLayers: [] }),
    clearOrthoLayerGroupIds: () => setState({ activeOrthoLayerGroupIds: [] }),
    setHoveredLayerId: (hoveredLayerIdPrefix) => {
      const { hoveredLayerId } = getState()
      if (hoveredLayerId !== hoveredLayerIdPrefix) {
        setState({ hoveredLayerId: hoveredLayerIdPrefix })
      }
    },
    setMapBbox: (bbox) => setState({ mapBbox: bbox }),
    setPointViewPort: (viewPort) => setState({ pointViewPort: viewPort }),
  }),
})

export const useSetVisualizationMode = () =>
  useMapStore((s) => s.setVisualizationMode)
export const useSetActiveLayers = () => useMapStore((s) => s.setActiveLayers)
export const useSetBaseLayer = () => useMapStore((s) => s.setBaseLayer)
export const useSetCurrentMapBounds = () =>
  useMapStore((s) => s.setCurrentMapBounds)
export const useToggleSelectedHexGridId = () =>
  useMapStore((s) => s.toggleSelectedHexGridId)
export const useSetHoveredHexGridId = () =>
  useMapStore((s) => s.setHoveredHexGridId)
export const useSetSelectedPointId = () =>
  useMapStore((s) => s.setSelectedPointId)
export const useSetSelectedExtentIds = () =>
  useMapStore((s) => s.setSelectedExtentIds)
export const useSetHoveredExtentIds = () =>
  useMapStore((s) => s.setHoveredExtentIds)
export const useSetGridCellProperties = () =>
  useMapStore((s) => s.setGridCellProperties)
export const useSetActiveOrthoLayerGroupIds = () =>
  useMapStore((s) => s.setActiveOrthoLayerGroupIds)
export const useToggleOrthoLayerGroupId = () =>
  useMapStore((s) => s.toggleOrthoLayerGroupId)
export const useClearActiveLayers = () =>
  useMapStore((s) => s.clearActiveLayers)
export const useClearOrthoLayerGroupIds = () =>
  useMapStore((s) => s.clearOrthoLayerGroupIds)
export const useSetHoveredLayerId = () =>
  useMapStore((s) => s.setHoveredLayerId)
export const useSetMapBbox = () => useMapStore((s) => s.setMapBbox)
export const useSetPointViewPort = () => useMapStore((s) => s.setPointViewPort)
export const useZoomToOrthoLayer = () => useMapStore((s) => s.zoomToOrthoLayer)

export const useVisualizationMode = () =>
  useMapStore((s) => s.visualizationMode)
export const useActiveLayers = () => useMapStore((s) => s.activeLayers)
export const useBaseLayer = () => useMapStore((s) => s.baseLayer)
export const useCurrentMapBounds = () => useMapStore((s) => s.currentMapBounds)
export const useSelectedHexGridId = () =>
  useMapStore((s) => s.selectedHexGridId)
export const useHoveredHexGridId = () => useMapStore((s) => s.hoveredHexGridId)
export const useSelectedPointId = () => useMapStore((s) => s.selectedPointId)
export const useSelectedExtentIds = () =>
  useMapStore((s) => s.selectedExtentIds)
export const useHoveredExtentIds = () => useMapStore((s) => s.hoveredExtentIds)
export const useGridCellProperties = () =>
  useMapStore((s) => s.gridCellProperties)
export const useActiveOrthoLayerGroupIds = () =>
  useMapStore((s) => s.activeOrthoLayerGroupIds)
export const useHoveredLayerId = () => useMapStore((s) => s.hoveredLayerId)
export const useMapBbox = () => useMapStore((s) => s.mapBbox)
export const usePointViewPort = () => useMapStore((s) => s.pointViewPort)
export const useZoomOrthoLayerGroupId = () =>
  useMapStore((s) => s.zoomOrthoLayerGroupId)
